import React, { useEffect, useMemo, useState } from 'react';
import { Minus, Plus, ShoppingCart, MapPin, CheckCircle, Loader2, Landmark, MessageCircle } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { formatPrice } from '../utils/catalog';
import { PAYMENT_OPTIONS, cartQty, cartTotal, publicOrderError, setCartQty, validateOrderForm, whatsappLink } from '../utils/publicOrder';

const inputClass = 'w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-white/40';

// Minisitio de un negocio de pedidos (agua, tienda, distribuidora): el cliente arma su pedido sin cuenta ni WhatsApp.
// El pedido entra a la app del negocio (origen «web») y lo ven sus domiciliarios; el precio lo calcula la base.
export default function PublicOrderPortal({ business, slug, themeColor = '#10b981' }) {
  const [catalog, setCatalog] = useState(null); // null = cargando
  const [cart, setCart] = useState([]);
  const [form, setForm] = useState({ name: '', phone: '', city: '', address: '', payment: 'efectivo', notes: '', website: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null); // { codigo, numero, total, payment }

  // Catálogo: al entrar muestra los precios públicos; si el cliente escribe su celular (10 dígitos o más) se pide de nuevo con él, y la base responde con la lista
  // de precios que tenga asignada en Clientes (p. ej. empresa). Si borra el celular vuelve a los precios públicos. El precio lo vuelve a calcular la base al enviar el pedido.
  const phoneDigits = form.phone.replace(/\D/g, '');
  const priceKey = phoneDigits.length >= 10 ? phoneDigits : '';
  useEffect(() => {
    let alive = true;
    const run = () => supabase.rpc('public_order_catalog', priceKey ? { p_slug: slug, p_phone: priceKey } : { p_slug: slug }).then(({ data, error: err }) => {
      if (!alive) return;
      if (err || !data?.habilitado) {
        // Un fallo al cambiar de precios no tumba el catálogo que ya se estaba viendo.
        setCatalog((current) => (current && current.habilitado ? current : { habilitado: false, productos: [], ciudades: [] }));
        return;
      }
      setCatalog(data);
    }).catch(() => { if (alive) setCatalog((current) => (current && current.habilitado ? current : { habilitado: false, productos: [], ciudades: [] })); });
    const timer = setTimeout(run, priceKey ? 500 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [slug, priceKey]);

  const products = useMemo(() => (Array.isArray(catalog?.productos) ? catalog.productos : []), [catalog]);
  const cities = Array.isArray(catalog?.ciudades) ? catalog.ciudades : [];
  const total = cartTotal(cart, products);
  const whatsapp = catalog?.whatsapp || business?.id_whatsapp || '';
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.website) return; // campo trampa: solo lo llenan programas
    const check = validateOrderForm(form, { cities, cart });
    if (!check.ok) { setError(check.error); return; }
    setBusy(true);
    const { data, error: err } = await supabase.rpc('create_public_order', {
      p_slug: slug, p_name: form.name, p_phone: form.phone, p_city: form.city, p_address: form.address, p_payment: form.payment, p_notes: form.notes,
      p_items: cart.map((l) => ({ id: l.id, cantidad: l.cantidad }))
    });
    setBusy(false);
    if (err) { setError(publicOrderError(err)); return; }
    setDone({ codigo: data?.codigo, numero: data?.numero || null, total: Number(data?.total) || total, payment: form.payment });
    setCart([]);
  };

  const header = (
    <header className="px-4 py-8 text-center space-y-2" style={{ background: `linear-gradient(180deg, ${themeColor}33, transparent)` }}>
      {business.logo_url && <img src={business.logo_url} alt="" className="w-20 h-20 rounded-2xl object-cover mx-auto border border-white/10" />}
      <h1 className="text-3xl font-black tracking-tight">{business.nombre_negocio}</h1>
      <p className="text-sm text-slate-300">Haz tu pedido aquí y te lo llevamos a la puerta.</p>
    </header>
  );

  if (catalog === null) {
    return <div className="min-h-screen bg-[#09090b] text-white flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  if (!catalog.habilitado || products.length === 0) {
    return (
      <div className="min-h-screen bg-[#09090b] text-white font-sans">
        {header}
        <p className="text-center text-slate-300 px-6">Por ahora no puedes pedir por esta página.</p>
        {whatsapp && <a href={whatsappLink(whatsapp, 'Hola, quiero hacer un pedido')} className="block mx-auto mt-4 w-fit rounded-xl px-5 py-3 font-bold text-black" style={{ backgroundColor: themeColor }}>Pedir por WhatsApp</a>}
      </div>
    );
  }

  if (done) {
    const bank = catalog.datos_pago;
    return (
      <div className="min-h-screen bg-[#09090b] text-white font-sans pb-16">
        {header}
        <main className="max-w-md mx-auto px-4 space-y-4" role="status">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center space-y-2">
            <CheckCircle className="mx-auto" size={40} style={{ color: themeColor }} />
            <h2 className="text-xl font-black">¡Pedido recibido!</h2>
            <p className="text-sm text-slate-300">{done.numero ? 'Tu número de pedido es' : 'Tu código es'} <strong className="font-mono text-white">{done.numero ? `#${done.numero}` : done.codigo}</strong></p>
            <p className="text-sm text-slate-300">Total a pagar: <strong className="text-white">{formatPrice(done.total)}</strong></p>
          </div>
          {done.payment === 'transferencia' ? (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-2">
              <p className="text-sm font-bold flex items-center gap-2"><Landmark size={16} /> Cómo pagar por transferencia</p>
              {done.numero && <p className="text-xs text-slate-300">Al enviar el comprobante, escribe el número de tu pedido: <strong className="text-white">#{done.numero}</strong>.</p>}
              {bank ? <p className="text-sm text-slate-200 whitespace-pre-line">{bank}</p> : <p className="text-sm text-slate-300">El negocio te enviará los datos para pagar por WhatsApp.</p>}
              {whatsapp && (
                <a href={whatsappLink(whatsapp, `Hola, soy ${form.name}. Pedido ${done.numero ? `#${done.numero}` : done.codigo}. Te envío el comprobante de la transferencia.`)} className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold text-black" style={{ backgroundColor: themeColor }}>
                  <MessageCircle size={16} /> Enviar comprobante por WhatsApp
                </a>
              )}
            </div>
          ) : (
            <p className="text-sm text-center text-slate-300">Pagas en efectivo cuando recibas tu pedido.</p>
          )}
          <button type="button" onClick={() => { setDone(null); setForm((f) => ({ ...f, notes: '' })); }} className="w-full rounded-xl border border-white/10 py-3 text-sm font-bold">Hacer otro pedido</button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] text-white font-sans pb-16">
      {header}
      <form onSubmit={submit} className="max-w-md mx-auto px-4 space-y-6" noValidate>
        <section className="space-y-3" aria-label="Productos">
          <h2 className="text-sm font-black uppercase tracking-wider text-slate-300">1. Elige tus productos</h2>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-2">
            <label htmlFor="po-phone-prices" className="text-xs font-bold text-slate-200">¿Ya eres cliente de empresa? Escribe tu celular para ver tu precio</label>
            <input id="po-phone-prices" className={inputClass} placeholder="Tu celular (300 123 4567)" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={set('phone')} />
            {catalog?.lista?.personalizada && <p role="status" className="text-xs font-bold" style={{ color: themeColor }}>Precios «{catalog.lista.nombre}» aplicados a tu celular.</p>}
            {priceKey && !catalog?.lista?.personalizada && <p className="text-[11px] text-slate-400">Este celular no tiene precios especiales: se muestran los precios públicos.</p>}
          </div>
          {products.map((p) => {
            const qty = cartQty(cart, p.id);
            return (
              <div key={p.id} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{p.nombre}</p>
                  <p className="text-sm" style={{ color: themeColor }}>{formatPrice(p.precio)}{p.unidad ? <span className="text-slate-400"> / {p.unidad}</span> : null}</p>
                  {!p.disponible && <p className="text-xs text-rose-400">Agotado</p>}
                </div>
                <button type="button" aria-label={`Menos ${p.nombre}`} disabled={qty === 0} onClick={() => setCart((c) => setCartQty(c, p.id, qty - 1))} className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center disabled:opacity-30"><Minus size={16} /></button>
                <span className="w-6 text-center font-black" aria-label={`Cantidad ${p.nombre}`}>{qty}</span>
                <button type="button" aria-label={`Más ${p.nombre}`} disabled={!p.disponible} onClick={() => setCart((c) => setCartQty(c, p.id, qty + 1))} className="w-9 h-9 rounded-full flex items-center justify-center text-black disabled:opacity-30" style={{ backgroundColor: themeColor }}><Plus size={16} /></button>
              </div>
            );
          })}
        </section>

        <section className="space-y-3" aria-label="Tus datos">
          <h2 className="text-sm font-black uppercase tracking-wider text-slate-300">2. ¿A dónde te lo llevamos?</h2>
          <input aria-label="Tu nombre" className={inputClass} placeholder="Tu nombre" autoComplete="name" value={form.name} onChange={set('name')} />
          <input aria-label="Tu celular" className={inputClass} placeholder="Tu celular (300 123 4567)" type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={set('phone')} />
          {cities.length > 0 && (
            <select aria-label="Tu ciudad" className={inputClass} value={form.city} onChange={set('city')}>
              <option value="">Elige tu ciudad</option>
              {cities.map((c) => <option key={c} value={c} className="text-black">{c}</option>)}
            </select>
          )}
          <div className="relative">
            <MapPin size={16} className="absolute left-3 top-3.5 text-slate-500" />
            <input aria-label="Dirección de entrega" className={`${inputClass} pl-9`} placeholder="Dirección (barrio, calle, número, apto)" autoComplete="street-address" value={form.address} onChange={set('address')} />
          </div>
          <textarea aria-label="Notas del pedido" rows={2} className={inputClass} placeholder="Notas para el domiciliario (opcional)" value={form.notes} onChange={set('notes')} />
          <input aria-hidden="true" tabIndex={-1} autoComplete="off" name="website" value={form.website} onChange={set('website')} className="hidden" />
        </section>

        <section className="space-y-3" aria-label="Forma de pago">
          <h2 className="text-sm font-black uppercase tracking-wider text-slate-300">3. ¿Cómo vas a pagar?</h2>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Forma de pago">
            {PAYMENT_OPTIONS.map((o) => (
              <button key={o.value} type="button" role="radio" aria-checked={form.payment === o.value} onClick={() => setForm((f) => ({ ...f, payment: o.value }))}
                className={`rounded-xl border p-3 text-left text-sm ${form.payment === o.value ? 'border-white/60 bg-white/10' : 'border-white/10 bg-white/5'}`}>
                <span className="font-bold block">{o.label}</span>
                <span className="text-[11px] text-slate-400">{o.hint}</span>
              </button>
            ))}
          </div>
          {form.payment === 'transferencia' && (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm space-y-1">
              <p className="font-bold flex items-center gap-2"><Landmark size={16} /> Datos para transferir</p>
              {catalog.datos_pago ? <p className="text-slate-200 whitespace-pre-line">{catalog.datos_pago}</p> : <p className="text-slate-300">Al enviar el pedido te diremos cómo pagar por WhatsApp.</p>}
              <p className="text-[11px] text-slate-400">Cuando termines el pedido podrás enviar el comprobante por WhatsApp.</p>
            </div>
          )}
        </section>

        {error && <p role="alert" className="rounded-xl bg-rose-500/10 border border-rose-500/30 p-3 text-sm text-rose-300">{error}</p>}

        <div className="sticky bottom-0 bg-[#09090b]/95 backdrop-blur py-3 border-t border-white/10 -mx-4 px-4">
          <button type="submit" disabled={busy || cart.length === 0} className="w-full rounded-xl py-3.5 font-black text-black flex items-center justify-center gap-2 disabled:opacity-40" style={{ backgroundColor: themeColor }}>
            {busy ? <Loader2 size={18} className="animate-spin" /> : <ShoppingCart size={18} />}
            {cart.length === 0 ? 'Elige un producto' : `Enviar pedido · ${formatPrice(total)}`}
          </button>
          {whatsapp && <a href={whatsappLink(whatsapp, 'Hola, quiero hacer un pedido')} className="block text-center text-xs text-slate-400 mt-2 underline">¿Prefieres pedir por WhatsApp?</a>}
        </div>
      </form>
    </div>
  );
}
